import React from 'react';
import styled from 'styled-components';
import type { StatusBarItem } from '../layout/StatusBar';
import { AppShell } from '../layout/AppShell';
import type { DocumentDate, DocumentOverviewItem, DocumentSummaryStats, TaxonomyTreeNode } from '../types';
import type { SummaryAction } from '../components/DocumentSummaryHeader';
import { DocumentOverview } from '../components/DocumentOverview';
import { FirstSeenCard } from '../components/FirstSeenCard';
import { useFirstSeen } from '../hooks/useFirstSeen';
import type { PaginationModel } from '../types/table';
import type { DateRangeValue } from '../components/DocumentTable';

interface OverviewSummaryConfig {
    title: string;
    subtitle?: string;
    stats: DocumentSummaryStats;
    reviewStatusLabel?: string;
    lastUpdatedAt?: string;
    lastUpdatedBy?: string;
    actions?: SummaryAction[];
    onDuplicatesClick?: () => void;
    datasetPath?: string;
    filevineProjectId?: number | null;
    filevineProjectName?: string | null;
}

interface OverviewScreenProps {
    caseTitle: string;
    caseSubtitle?: string;
    statusItems: StatusBarItem[];
    summary: OverviewSummaryConfig;
    documents: DocumentOverviewItem[];
    onDocumentOpen: (docId: string) => void;
    onDocumentAction?: (actionId: string, docId: string) => void;
    onDocumentRename?: (docId: string, title: string) => Promise<void> | void;
    selectedDocumentId?: string | null;
    emptyStateMessage?: string;
    onBackToCases: () => void;
    actionsSlot?: React.ReactNode;
    thumbnailsByDoc?: Record<string, (string | null)[]>;
    onRequestThumbnails?: (document: DocumentOverviewItem) => void;
    onWarningClick?: (warningId: string, docId: string) => void;
    tagPool?: string[];
    onTagsChange?: (docId: string, tags: string[]) => void | Promise<void>;
    onCreateTag?: (tag: string) => void | Promise<void>;
    onDocumentDateChange?: (docId: string, date: DocumentDate | null) => void | Promise<void>;
    paginationModel: PaginationModel;
    onPaginationModelChange: (model: PaginationModel) => void;
    rowCount: number;
    isLoadingDocuments?: boolean;
    searchQuery: string;
    onSearchQueryChange: (value: string) => void;
    sortOption: string;
    onSortOptionChange: (value: string) => void;
    activeStatus?: string | null;
    onStatusFilterChange?: (status: string | null) => void;
    activeCategory?: string | null;
    onCategoryFilterChange?: (value: string | null) => void;
    categoryTree?: TaxonomyTreeNode[];
    dateRange?: DateRangeValue | null;
    onDateRangeChange?: (range: DateRangeValue | null) => void;
    duplicateFilterDocId?: string | null;
    onDuplicateFilterChange?: (docId: string | null) => void;
    selectedDocIds?: string[];
    onToggleSelection?: (
        docId: string,
        opts: { range?: boolean; visibleDocIds: string[] },
    ) => void;
    onToggleSelectAllOnPage?: (visibleDocIds: string[], select: boolean) => void;
    onClearSelection?: () => void;
    onMergeSelected?: () => void;
    isMergingDocs?: boolean;
    jobsIndicator?: React.ReactNode;
}


const ScreenContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    animation: fadeIn 0.28s ease;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(8px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

export const OverviewScreen: React.FC<OverviewScreenProps> = ({
    caseTitle,
    caseSubtitle,
    statusItems,
    summary,
    documents,
    onDocumentOpen,
    onDocumentAction,
    onDocumentRename,
    selectedDocumentId,
    emptyStateMessage,
    onBackToCases,
    actionsSlot,
    thumbnailsByDoc,
    onRequestThumbnails,
    onWarningClick,
    tagPool,
    onTagsChange,
    onCreateTag,
    onDocumentDateChange,
    paginationModel,
    onPaginationModelChange,
    rowCount,
    isLoadingDocuments = false,
    searchQuery,
    onSearchQueryChange,
    sortOption,
    onSortOptionChange,
    activeStatus = null,
    onStatusFilterChange,
    activeCategory = null,
    onCategoryFilterChange,
    categoryTree,
    dateRange,
    onDateRangeChange,
    duplicateFilterDocId,
    onDuplicateFilterChange,
    selectedDocIds,
    onToggleSelection,
    onToggleSelectAllOnPage,
    onClearSelection,
    onMergeSelected,
    isMergingDocs,
    jobsIndicator,
}) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    // Defer the Filevine card until the case-view intro is dismissed so the
    // two cards (same anchor) never render stacked on a first visit.
    const caseViewCard = useFirstSeen('case_view');
    const showFilevineCard =
        typeof summary.filevineProjectId === 'number' && !caseViewCard.shouldShow;
    return (
        <AppShell
            breadcrumb={[
                { label: 'Cases', onClick: onBackToCases },
                { label: caseTitle ?? '' },
            ]}
            statusItems={statusItems}
            jobsIndicator={jobsIndicator}
        >
            <ScreenContainer ref={containerRef}>
                <DocumentOverview
                    summary={summary}
                    documents={documents}
                    onDocumentAction={(action, docId) => {
                        if (action === 'open') {
                            onDocumentOpen(docId);
                        }
                        onDocumentAction?.(action, docId);
                    }}
                    onDocumentRename={onDocumentRename}
                    selectedDocumentId={selectedDocumentId ?? undefined}
                    emptyStateMessage={emptyStateMessage}
                    thumbnailsByDoc={thumbnailsByDoc}
                    onRequestThumbnails={onRequestThumbnails}
                    onWarningClick={onWarningClick}
                    tagPool={tagPool}
                    onTagsChange={onTagsChange}
                    onCreateTag={onCreateTag}
                    onDocumentDateChange={onDocumentDateChange}
                    paginationModel={paginationModel}
                    onPaginationModelChange={onPaginationModelChange}
                    rowCount={rowCount}
                    isLoading={isLoadingDocuments}
                    searchQuery={searchQuery}
                    onSearchQueryChange={onSearchQueryChange}
                    sortOption={sortOption}
                    onSortOptionChange={onSortOptionChange}
                    activeStatus={activeStatus}
                    onStatusFilterChange={onStatusFilterChange}
                    activeCategory={activeCategory}
                    onCategoryFilterChange={onCategoryFilterChange}
                    categoryTree={categoryTree}
                    dateRange={dateRange}
                    onDateRangeChange={onDateRangeChange}
                    duplicateFilterDocId={duplicateFilterDocId}
                    onDuplicateFilterChange={onDuplicateFilterChange}
                    selectedDocIds={selectedDocIds}
                    onToggleSelection={onToggleSelection}
                    onToggleSelectAllOnPage={onToggleSelectAllOnPage}
                    onClearSelection={onClearSelection}
                    onMergeSelected={onMergeSelected}
                    isMergingDocs={isMergingDocs}
                />
            </ScreenContainer>
            <FirstSeenCard
                cardKey="case_view"
                targetRef={containerRef}
                title="You're looking at a case"
                placement="bottom"
            >
                <p>
                    A case is a collection of documents pulled out of one or
                    more uploaded PDFs.
                </p>
                <ul>
                    <li><strong>Search, sort, and filter</strong> the document list above — including by <strong>category</strong> (the intake-checklist section each document falls under). Documents matching your search by title or category rank first.</li>
                    <li><strong>Merge</strong> documents that were split apart by mistake.</li>
                    <li>Cases linked to <strong>Filevine</strong> can pull documents from the project and push reviewed results back — see the actions in the case header.</li>
                    <li>
                        <strong>Heads up:</strong> titles, dates, and categories
                        are inferred from the page text by the AI and aren&apos;t
                        100% accurate. Spot-check anything important.
                    </li>
                </ul>
            </FirstSeenCard>
            {showFilevineCard && (
                <FirstSeenCard
                    cardKey="filevine_case"
                    targetRef={containerRef}
                    title="This case is linked to Filevine"
                    placement="bottom"
                >
                    <p>Two actions in the case header work with the linked project:</p>
                    <ul>
                        <li><strong>Pull from Filevine</strong> brings one chosen document into this case — downloaded, split into documents, and analyzed automatically. Its Filevine upload date is recorded (the <strong>Uploaded</strong> column).</li>
                        <li><strong>Push to Filevine</strong> sends the reviewed, split documents back to the project as new PDFs (plus an index) in a &ldquo;pdf_processing_output&rdquo; folder. It only adds files — the original document is never changed.</li>
                    </ul>
                </FirstSeenCard>
            )}
        </AppShell>
    );
};
